import styled from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";

import { colors } from "theme/colors";

/**
 * The page's horizontal frame. Shared with the standalone SiteHeader so the nav
 * sits on the same edge as the content below it.
 *
 * 8vw rather than a smaller ratio: at ~1240px a 5vw gutter left the sections
 * running into the edges of the screen. The 100px cap stops that becoming a huge
 * inset on large monitors, so the gutter grows where it is needed and holds
 * steady past ~1250px. The max-width only bites on ultrawide.
 */
export const PAGE_GUTTER = "clamp(20px, 8vw, 100px)";
export const PAGE_MAX_WIDTH = "1920px";

// Allstar brand green. Autocapture unlocks at Standard, but the page wears the
// house accent rather than the tier colour.
export const ACCENT = colors.envy;
export const ACCENT_DEEP = colors.darkerEnvy;
export const ACCENT_SOFT = colors.envyAlpha10;
export const ACCENT_BORDER = colors.envyAlpha40;

// Allstar's display voice: heavy italic uppercase. Barlow 900 italic is loaded
// in index.css for this.
export const Display = styled(Text)`
  font-style: italic;
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.04;
  text-transform: uppercase;
`;

// Ramp lifted from the Autocapture logo so headline and mark read as one system
export const GradientText = styled.span`
  background: linear-gradient(100deg, ${ACCENT_DEEP}, #6bc3ab 55%, #3a9cc3);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
`;

// Same frame as the standalone SiteHeader, so nav and sections share one edge.
export const Section = styled(Flex)`
  flex-direction: column;
  margin: 0 auto;
  max-width: ${PAGE_MAX_WIDTH};
  padding-left: ${PAGE_GUTTER};
  padding-right: ${PAGE_GUTTER};
  width: 100%;
`;

export const SectionHeading = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-style: italic;
  font-weight: 900;
  letter-spacing: -0.02em;
  text-transform: uppercase;
`;

SectionHeading.defaultProps = {
  fontSize: [28, 34, 42, 50],
  lineHeight: 1.05,
};

export const SectionSubheading = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-weight: 500;
  max-width: 62ch;
`;

SectionSubheading.defaultProps = {
  fontSize: [17, 18, 20],
  lineHeight: 1.6,
  mt: 4,
};

export const HeroBackground = styled(Box)`
  background: radial-gradient(
    circle at 70% 0%,
    rgba(77, 189, 148, 0.14) 0%,
    rgba(13, 27, 37, 1) 45%,
    rgba(14, 17, 27, 1) 100%
  );
  width: 100%;
`;

// The separator is the colour change itself: Side by side sits on a lighter
// band so the edge reads without any rule, wave or gradient.
export const ContrastBand = styled(Box)`
  background: ${({ theme }) => theme.colors.navy300};
  width: 100%;
`;

export const Divider = styled(Box)`
  background: ${({ theme }) => theme.colors.navy};
  height: 1px;
  width: 100%;
`;

// A hand-drawn feeling underline for a single word inside a display heading.
// Sits below the baseline so descenders are never crossed.
export const Underlined = styled.span`
  display: inline-block;
  position: relative;
  white-space: nowrap;

  ::after {
    background: linear-gradient(90deg, ${ACCENT_DEEP}, ${ACCENT} 55%, #8fe9c4);
    border-radius: 3px;
    bottom: -0.1em;
    content: "";
    height: 0.09em;
    left: 0;
    position: absolute;
    right: 0;
  }
`;

// Shared block type, so every section below the hero sets titles, body and
// eyebrow labels identically.
export const BlockTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 27px;
  font-weight: 700;
`;

export const BlockBody = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 18px;
  line-height: 1.6;
  margin-top: 16px;
`;

export const MetaLabel = styled.div`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

// Native <details>: the repo has no accordion primitive and this needs no JS.
export const FaqItem = styled.details`
  border-bottom: 1px solid ${({ theme }) => theme.colors.navy50};

  summary {
    align-items: center;
    color: ${({ theme }) => theme.colors.ice};
    cursor: pointer;
    display: flex;
    font-size: 19px;
    font-weight: 600;
    gap: 16px;
    justify-content: space-between;
    list-style: none;
    padding: 22px 4px;
  }

  summary::-webkit-details-marker {
    display: none;
  }

  summary::after {
    color: ${ACCENT};
    content: "+";
    flex: none;
    font-size: 24px;
    font-weight: 400;
    transition: transform 0.25s ease;
  }

  &[open] summary::after {
    transform: rotate(45deg);
  }

  .answer {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 16.5px;
    line-height: 1.6;
    max-width: 68ch;
    padding: 0 4px 22px;
  }
`;
